import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { messagesAPI } from '../lib/api';
import { formatDate, formatRelativeTime, formatPrice } from '../lib/utils';
import { toast } from 'sonner';
import {
  MessageSquare,
  Send,
  Mail,
  Phone,
  ExternalLink,
  ArrowLeft,
  Image,
  Clock,
  Check,
  CheckCheck,
  Trash2,
  Archive,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

export default function MessagesPage() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const messagesEndRef = useRef(null);

  // Fetch conversations
  useEffect(() => {
    const fetchConversations = async () => {
      setLoading(true);
      try {
        const response = await messagesAPI.getConversations();
        setConversations(response.data);
      } catch (error) {
        console.error('Error fetching conversations:', error);
        toast.error('No se pudieron cargar las conversaciones');
      } finally {
        setLoading(false);
      }
    };
    fetchConversations();
  }, []);

  const reloadConversations = async () => {
    try {
      const response = await messagesAPI.getConversations();
      setConversations(response.data);
    } catch (error) {
      console.error('Error reloading conversations:', error);
    }
  };

  const handleCleanupOld = async () => {
    setCleaning(true);
    try {
      const { data } = await messagesAPI.cleanupOldConversations(30);
      if (data.deleted_conversations === 0) {
        toast.info('No hay conversaciones de más de 30 días');
      } else {
        toast.success(
          `${data.deleted_conversations} conversación(es) eliminada(s) (${data.deleted_messages} mensajes)`
        );
        if (
          selectedConversation &&
          !conversations.find((c) => c.id === selectedConversation.id)
        ) {
          setSelectedConversation(null);
          setConversationMessages([]);
        }
        await reloadConversations();
      }
    } catch (error) {
      console.error('Error cleaning conversations:', error);
      toast.error('No se pudieron eliminar las conversaciones antiguas');
    } finally {
      setCleaning(false);
    }
  };

  const handleDeleteOne = async (conversationId, e) => {
    if (e) {
      e.stopPropagation();
    }
    try {
      await messagesAPI.deleteConversation(conversationId);
      toast.success('Conversación eliminada');
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
        setConversationMessages([]);
      }
      setConversations((prev) => prev.filter((c) => c.id !== conversationId));
    } catch (error) {
      console.error('Error deleting conversation:', error);
      toast.error('No se pudo eliminar la conversación');
    }
  };

  // Fetch messages when conversation is selected
  useEffect(() => {
    if (!selectedConversation) return;
    
    const fetchMessages = async () => {
      setLoadingMessages(true);
      try {
        const response = await messagesAPI.getConversationMessages(selectedConversation.id);
        setConversationMessages(response.data);
        
        // Update unread count in conversations list
        setConversations(prev => prev.map(conv => 
          conv.id === selectedConversation.id ? { ...conv, unread_count: 0 } : conv
        ));
      } catch (error) {
        console.error('Error fetching messages:', error);
        toast.error('No se pudieron cargar los mensajes');
      } finally {
        setLoadingMessages(false);
      }
    };
    fetchMessages();
  }, [selectedConversation?.id]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversationMessages]);

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim() || !selectedConversation) return;
    
    setSendingReply(true);
    try {
      // Find the other participant
      const otherParticipantId = selectedConversation.participant_ids.find(id => id !== user.id);
      
      const response = await messagesAPI.send({
        listing_id: selectedConversation.listing_id,
        receiver_id: otherParticipantId,
        message: replyMessage.trim(),
        conversation_id: selectedConversation.id,
      });
      
      // Add new message to the list
      setConversationMessages(prev => [...prev, response.data]);
      
      // Update conversation in list
      setConversations(prev => prev.map(conv => 
        conv.id === selectedConversation.id 
          ? { 
              ...conv, 
              last_message: replyMessage.trim().slice(0, 100),
              last_message_time: new Date().toISOString(),
              last_sender_id: user.id
            } 
          : conv
      ));
      
      setReplyMessage('');
      toast.success('Mensaje enviado');
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('No se pudo enviar el mensaje');
    } finally {
      setSendingReply(false);
    }
  };

  const getOtherParticipantName = (conversation) => {
    if (conversation?.other_user_name) return conversation.other_user_name;
    const otherId = conversation?.participant_ids?.find((id) => id !== user?.id);
    return conversation?.participant_names?.[otherId] || 'Desconocido';
  };

  const totalUnread = conversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary p-8">
        <div className="max-w-[1400px] mx-auto">
          <Skeleton className="h-10 w-64 mb-8" />
          <div className="grid md:grid-cols-12 gap-6">
            <div className="md:col-span-4 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-24" />
              ))}
            </div>
            <div className="md:col-span-8">
              <Skeleton className="h-[600px]" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary" data-testid="messages-page">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-6">
          <div className="flex items-center gap-4 mb-4">
            <Link to="/dashboard">
              <Button variant="ghost" className="text-muted-foreground">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver al panel
              </Button>
            </Link>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">Mensajes</h1>
              <p className="text-muted-foreground mt-1">
                {totalUnread > 0
                  ? `${totalUnread} mensaje${totalUnread > 1 ? 's' : ''} sin leer`
                  : '¡Estás al día!'}
              </p>
            </div>

            {conversations.length > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="border-destructive/30 text-destructive hover:bg-destructive/5"
                    disabled={cleaning}
                    data-testid="cleanup-old-conversations-btn"
                  >
                    <Archive className="h-4 w-4 mr-2" />
                    {cleaning ? 'Limpiando...' : 'Limpiar antiguas (30+ días)'}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Eliminar conversaciones antiguas?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Se eliminarán todas las conversaciones sin actividad en los últimos 30 días,
                      junto con sus mensajes. Esta acción no se puede deshacer.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleCleanupOld}
                      className="bg-destructive text-white hover:bg-destructive/90"
                      data-testid="confirm-cleanup-btn"
                    >
                      Sí, eliminar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
        <div className="grid md:grid-cols-12 gap-6 h-[calc(100vh-280px)] min-h-[600px]">
          {/* Conversations List */}
          <div className="md:col-span-4 bg-white border border-border rounded-md overflow-hidden flex flex-col">
            <div className="p-4 border-b border-border bg-secondary/30">
              <h2 className="font-heading text-lg font-bold uppercase tracking-tight">Conversaciones</h2>
            </div>
            <div className="flex-1 overflow-y-auto">
              {conversations.length > 0 ? (
                conversations.map((conv) => (
                  <div
                    key={conv.id}
                    onClick={() => setSelectedConversation(conv)}
                    className={`group p-4 border-b border-border cursor-pointer transition-all hover:bg-secondary/50 ${
                      selectedConversation?.id === conv.id ? 'bg-accent/5 border-l-4 border-l-accent' : ''
                    }`}
                    data-testid={`conversation-${conv.id}`}
                  >
                    <div className="flex gap-3">
                      {/* Listing Image */}
                      <div className="w-14 h-14 rounded-sm overflow-hidden flex-shrink-0 bg-secondary">
                        {conv.listing_image ? (
                          <img 
                            src={conv.listing_image} 
                            alt={conv.listing_title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Image className="h-6 w-6 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-1 gap-2">
                          <span className="font-semibold text-sm truncate flex-1">
                            {getOtherParticipantName(conv)}
                          </span>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <span className="text-xs text-muted-foreground">
                              {formatRelativeTime(conv.last_message_time)}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => handleDeleteOne(conv.id, e)}
                              className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all p-1 -mr-1"
                              title="Eliminar conversación"
                              aria-label="Eliminar conversación"
                              data-testid={`delete-conversation-${conv.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground truncate mb-1">
                          {conv.listing_title}
                        </p>
                        <div className="flex items-center gap-2">
                          <p className={`text-sm truncate flex-1 ${conv.unread_count > 0 ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                            {conv.last_sender_id === user?.id && <span className="text-muted-foreground">Tú: </span>}
                            {conv.last_message}
                          </p>
                          {conv.unread_count > 0 && (
                            <Badge className="bg-accent text-white text-xs px-1.5 py-0.5 flex-shrink-0">
                              {conv.unread_count}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center">
                  <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">Aún no tienes conversaciones</p>
                  <Link to="/browse" className="text-accent text-sm hover:underline mt-2 inline-block">
                    Explora anuncios para iniciar una conversación
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Conversation Detail / Chat View */}
          <div className="md:col-span-8 bg-white border border-border rounded-md overflow-hidden flex flex-col">
            {selectedConversation ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-border bg-secondary/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-sm overflow-hidden bg-secondary">
                        {selectedConversation.listing_image ? (
                          <img 
                            src={selectedConversation.listing_image} 
                            alt={selectedConversation.listing_title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Image className="h-5 w-5 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold">
                          {getOtherParticipantName(selectedConversation)}
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          {selectedConversation.listing_title}
                          {selectedConversation.listing_price && (
                            <span className="ml-2 font-semibold text-accent">
                              {formatPrice(selectedConversation.listing_price)}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <Link to={`/listings/${selectedConversation.listing_id}`}>
                      <Button variant="outline" size="sm">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Ver anuncio
                      </Button>
                    </Link>
                  </div>
                </div>

                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-secondary/20">
                  {loadingMessages ? (
                    <div className="space-y-4">
                      {[...Array(3)].map((_, i) => (
                        <Skeleton key={i} className="h-20 w-3/4" />
                      ))}
                    </div>
                  ) : (
                    <>
                      {conversationMessages.map((msg, index) => {
                        const isOwn = msg.sender_id === user?.id;
                        const showDate = index === 0 || 
                          new Date(msg.created_at).toDateString() !== 
                          new Date(conversationMessages[index - 1]?.created_at).toDateString();
                        
                        return (
                          <React.Fragment key={msg.id}>
                            {showDate && (
                              <div className="flex justify-center my-4">
                                <span className="text-xs text-muted-foreground bg-white px-3 py-1 rounded-full">
                                  {formatDate(msg.created_at)}
                                </span>
                              </div>
                            )}
                            <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                              <div className={`max-w-[70%] ${isOwn ? 'order-2' : ''}`}>
                                {/* Sender info for received messages */}
                                {!isOwn && index === 0 || (!isOwn && conversationMessages[index - 1]?.sender_id !== msg.sender_id) ? (
                                  <div className="flex items-center gap-2 mb-1 ml-1">
                                    <span className="text-xs font-semibold">{msg.sender_name}</span>
                                    {msg.sender_phone && (
                                      <a href={`tel:${msg.sender_phone}`} className="text-xs text-accent hover:underline flex items-center gap-1">
                                        <Phone className="h-3 w-3" />
                                        {msg.sender_phone}
                                      </a>
                                    )}
                                  </div>
                                ) : null}
                                
                                <div 
                                  className={`p-3 rounded-lg ${
                                    isOwn 
                                      ? 'bg-accent text-white rounded-br-none' 
                                      : 'bg-white border border-border rounded-bl-none'
                                  }`}
                                  data-testid={`message-bubble-${msg.id}`}
                                >
                                  <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                                </div>
                                <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : ''}`}>
                                  <span className="text-xs text-muted-foreground">
                                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                  {isOwn && (
                                    msg.read 
                                      ? <CheckCheck className="h-3 w-3 text-accent" />
                                      : <Check className="h-3 w-3 text-muted-foreground" />
                                  )}
                                </div>
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>

                {/* Contact Info Bar */}
                {selectedConversation && (
                  <div className="px-4 py-2 bg-secondary/50 border-t border-b border-border">
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-muted-foreground">Contacto:</span>
                      {conversationMessages[0]?.sender_id !== user?.id && conversationMessages[0]?.sender_email && (
                        <a href={`mailto:${conversationMessages[0].sender_email}`} className="flex items-center gap-1 text-accent hover:underline">
                          <Mail className="h-3 w-3" />
                          {conversationMessages[0].sender_email}
                        </a>
                      )}
                      {conversationMessages[0]?.sender_id !== user?.id && conversationMessages[0]?.sender_phone && (
                        <a href={`tel:${conversationMessages[0].sender_phone}`} className="flex items-center gap-1 text-accent hover:underline">
                          <Phone className="h-3 w-3" />
                          {conversationMessages[0].sender_phone}
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Reply Input */}
                <form onSubmit={handleSendReply} className="p-4 border-t border-border bg-white">
                  <div className="flex gap-3">
                    <Textarea
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      placeholder="Escribe tu mensaje..."
                      className="flex-1 min-h-[80px] resize-none"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendReply(e);
                        }
                      }}
                      data-testid="reply-input"
                    />
                    <Button 
                      type="submit" 
                      className="btn-accent self-end"
                      disabled={!replyMessage.trim() || sendingReply}
                      data-testid="send-reply-btn"
                    >
                      {sendingReply ? (
                        <Clock className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Enter para enviar · Shift+Enter para una nueva línea
                  </p>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageSquare className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="font-heading text-xl font-bold uppercase mb-2">Selecciona una conversación</h3>
                  <p className="text-muted-foreground">Elige una conversación de la lista para ver los mensajes</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
